// **The Match screen's statistics history** (`match-hud` **D-4**): the client half of
// `GET /live/stats`.
//
// Why a server series rather than client accretion, restated where the code is: the client cannot
// produce per-type series for the *opponent* at all (`StateHistogram` is own-colony), has no source
// for nectar **capacity** (`cells × per_cell_capacity`, a balance constant that is never sent), and
// its one accreted series (`matchTimeline`) is capped at 48 samples and compacted on `livingCount`
// extrema, so the nectar values that ride along are non-uniformly sampled. Above all: a projected
// screen must survive an accidental refresh, and an accreted series does not.
//
// The whole series is re-read on every poll. A cursor slice would be cheaper on the wire and worse
// where it matters — a refetch after a reload is exactly the case this exists for, so the simple
// shape is also the correct one. (`ponytail: full-series poll; add an `afterTick` slice if a longer
// match than 30 minutes ever makes the payload matter.)

import { useEffect, useState } from 'react';

/** One colony's row in a sample. Field names mirror the server DTO's camelCase exactly. */
export interface ColonyStatsSample {
  colonyId: number;
  /** Living population, queen included. */
  population: number;
  /** Living workers by bee-type **name**, queen excluded. */
  composition: Record<string, number>;
  storedNectar: number;
  /** `cells × per_cell_capacity` — the colony's pool ceiling. */
  capacity: number;
}

export interface StatsSample {
  tick: number;
  colonies: ColonyStatsSample[];
}

export interface StatsHistorySeams {
  /** The live session bearer. `null` means "not in a match" — nothing is requested at all. */
  token?: string | null;
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  /** Poll period, ms. */
  pollMs?: number;
}

function isRecordOfNumbers(value: unknown): value is Record<string, number> {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  return Object.values(value as Record<string, unknown>).every((v) => typeof v === 'number');
}

function decodeColony(value: unknown): ColonyStatsSample | null {
  if (value === null || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  if (
    typeof row.colonyId !== 'number' ||
    typeof row.population !== 'number' ||
    typeof row.storedNectar !== 'number' ||
    typeof row.capacity !== 'number' ||
    !isRecordOfNumbers(row.composition)
  ) {
    return null;
  }
  return {
    colonyId: row.colonyId,
    population: row.population,
    composition: row.composition,
    storedNectar: row.storedNectar,
    capacity: row.capacity,
  };
}

/** `null` for anything that is not a well-formed series — never a half-decoded one. */
export function decodeStatsHistory(value: unknown): StatsSample[] | null {
  if (value === null || typeof value !== 'object') return null;
  const body = value as Record<string, unknown>;
  if (!Array.isArray(body.samples)) return null;
  const samples: StatsSample[] = [];
  for (const entry of body.samples) {
    if (entry === null || typeof entry !== 'object') return null;
    const sample = entry as Record<string, unknown>;
    if (typeof sample.tick !== 'number' || !Array.isArray(sample.colonies)) return null;
    const colonies: ColonyStatsSample[] = [];
    for (const colony of sample.colonies) {
      const decoded = decodeColony(colony);
      if (decoded === null) return null;
      colonies.push(decoded);
    }
    samples.push({ tick: sample.tick, colonies });
  }
  return samples;
}

/**
 * Polls `GET /live/stats`, returning the whole series.
 *
 * A failed poll keeps the last readable series and tries again — a chart that blanks on one dropped
 * request would be worse than a chart a second stale.
 */
export function useStatsHistory({
  token = null,
  baseUrl = '',
  fetchImpl,
  pollMs = 5_000,
}: StatsHistorySeams): StatsSample[] {
  const [samples, setSamples] = useState<StatsSample[]>([]);

  useEffect(() => {
    if (token === null) {
      return undefined;
    }
    const doFetch = fetchImpl ?? globalThis.fetch?.bind(globalThis);
    if (doFetch === undefined) {
      return undefined;
    }
    let live = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const poll = async (): Promise<void> => {
      try {
        const response = await doFetch(`${baseUrl.replace(/\/+$/, '')}/live/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const decoded = response.ok ? decodeStatsHistory(await response.json()) : null;
        if (live && decoded !== null) {
          setSamples(decoded);
        }
      } catch {
        // Advisory UI: keep the last readable series.
      }
      if (live) timer = setTimeout(() => void poll(), pollMs);
    };
    void poll();
    return () => {
      live = false;
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [baseUrl, fetchImpl, pollMs, token]);

  return samples;
}

/** The most recent sample's row for `colonyId`, or `null` when the series has nothing for it. */
export function latestForColony(
  samples: readonly StatsSample[],
  colonyId: number,
): ColonyStatsSample | null {
  for (let i = samples.length - 1; i >= 0; i -= 1) {
    const row = samples[i].colonies.find((c) => c.colonyId === colonyId);
    if (row !== undefined) return row;
  }
  return null;
}

/** Every bee-type name seen for `colonyId` across the series, in first-seen order. */
export function typeNamesForColony(
  samples: readonly StatsSample[],
  colonyId: number,
): string[] {
  const names: string[] = [];
  for (const sample of samples) {
    const row = sample.colonies.find((c) => c.colonyId === colonyId);
    if (row === undefined) continue;
    for (const name of Object.keys(row.composition)) {
      if (!names.includes(name)) names.push(name);
    }
  }
  return names;
}
